import {
    cn,
    Field,
    FieldError,
    FieldLabel,
    FieldTitle,
    Input as DSInput,
    Radio,
    RadioGroup,
} from "frontend-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";

import { CharacterCount } from "@dashboard/components/CharacterCount";

import { useFieldContext } from "./Form";
import { FALLBACK_ICON, ICON_MAP } from "@dashboard/features/Dashboard/Shortcuts/iconMap";

const ICON_VALUES = [
    { value: "globe", key: "dashboard.shortcuts.iconGlobe" },
    { value: "file", key: "dashboard.shortcuts.iconFile" },
    { value: "envelope", key: "dashboard.shortcuts.iconEnvelope" },
    { value: "chart-bar", key: "dashboard.shortcuts.iconChartBar" },
    { value: "user", key: "dashboard.shortcuts.iconUser" },
    { value: "gear", key: "dashboard.shortcuts.iconGear" },
    { value: "link", key: "dashboard.shortcuts.iconLink" },
    { value: "bell", key: "dashboard.shortcuts.iconBell" },
    { value: "folder", key: "dashboard.shortcuts.iconFolder" },
    { value: "calendar", key: "dashboard.shortcuts.iconCalendar" },
] as const;

interface InputWithCharCountProps
    extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
    label?: string;
    maxLength?: number;
    showCharCount?: boolean;
    charCountWarningThreshold?: number;
}

export function Input(props: InputWithCharCountProps) {
    const field = useFieldContext<string>();
    const {
        label,
        className,
        maxLength,
        showCharCount = false,
        charCountWarningThreshold = 0.8,
        ...inputProps
    } = props;

    const isInvalid = field.state.meta.errors.length > 0;
    const currentLength = String(field.state.value ?? "").length;

    return (
        <Field data-invalid={isInvalid}>
            {label && (
                <FieldLabel htmlFor={field.name} className="text-sm font-medium">
                    {label}
                </FieldLabel>
            )}
            <DSInput
                id={field.name}
                name={field.name}
                className={className}
                maxLength={maxLength}
                {...inputProps}
                value={String(field.state.value ?? "")}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                aria-invalid={isInvalid}
            />
            {showCharCount && maxLength !== undefined && (
                <div className="flex justify-end">
                    <CharacterCount
                        currentLength={currentLength}
                        maxLength={maxLength}
                        warningThreshold={charCountWarningThreshold}
                    />
                </div>
            )}
            {isInvalid && <FieldError errors={field.state.meta.errors} />}
        </Field>
    );
}

export function IconSelect(props: { label?: string }) {
    const field = useFieldContext<string>();
    const { t } = useTranslation();

    return (
        <Field>
            {props.label && <FieldTitle>{props.label}</FieldTitle>}
            <RadioGroup
                value={field.state.value}
                onValueChange={field.handleChange}
                className="grid grid-cols-3 gap-2 sm:grid-cols-5"
            >
                {ICON_VALUES.map((opt) => (
                    <Radio.Root
                        key={opt.value}
                        value={opt.value}
                        className={cn(
                            "flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-[10px] text-compendia-foreground transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-compendia-foreground",
                            "border-compendia-border bg-compendia-card hover:bg-compendia-greige-10",
                            "data-[checked]:border-compendia-foreground data-[checked]:bg-compendia-greige-40",
                        )}
                    >
                        <FontAwesomeIcon
                            icon={ICON_MAP[opt.value] ?? FALLBACK_ICON}
                            className="size-[18px]"
                        />
                        <span>{t(opt.key)}</span>
                    </Radio.Root>
                ))}
            </RadioGroup>
        </Field>
    );
}
